import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { asyncScheduler, Observable, scheduled } from 'rxjs';

import { ErrorService } from '../../../../../shared/services/error-service';
import { ToastService } from '../../../../../shared/toast/toast.service';
import { WizardStep } from '../../../../../shared/controls/wizard-control/wizard-step.interface';

import { AnnexureRangeMappingInput, AnnexureService, WorkbookService } from '../../../../../../service/proxy';

@Component({
    selector: 'annexure-range-selection',
    templateUrl: './annexure-range-selection.component.html',
    styleUrls: ['./annexure-range-selection.component.scss'],

    imports: [
        CommonModule,
        FormsModule
    ],

    providers: [
        AnnexureService,
        WorkbookService
    ]
})
export class AnnexureRangeSelectionComponent implements OnInit, WizardStep {
    @ViewChild('form')
    form!: NgForm;

    public batchId: number = 0;
    public firstTenRows: any[] = [];
    public lastTenRows: any[] = [];
    public loading: boolean = false;
    public isWideTable = false;

    public model: AnnexureRangeMappingInput = new AnnexureRangeMappingInput();

    constructor(
        private route: ActivatedRoute,
        private annexureService: AnnexureService,
        private workbookService: WorkbookService,
        private toastService: ToastService,
        private errorService: ErrorService,
    ) {}

    ngOnInit(): void {
        // Data loaded in onActivate method
    }

    getBatch(): void {
        this.annexureService.getAnnexureBatch(this.batchId)
            .subscribe({
                next: (result: any) => {
                    this.getWorksheetSample(`${result.filePath}//${result.storedName}`);
                },
                error: (error: any) => {
                    console.log(error);
                },
            });
    }

    getImportRowMapping(): void {
        this.annexureService.getAnnexureImportRangeMapping(this.batchId)
            .subscribe((result) => {
                if (result) {
                    this.model = new AnnexureRangeMappingInput({
                        headerIndex: result.headerIndex || 1,
                        startIndex: result.startIndex || 2,
                        endIndex: result.endIndex,
                    });
                }
            });
    }

    getWorksheetSample(filePath: string): void {
        this.loading = true;
        this.isWideTable = false; 

        this.workbookService.getSampleBySheetIndex(filePath, 0)
            .subscribe({
                next: (result) => {
                    this.firstTenRows = result.firstTenRows || [];
                    this.lastTenRows = result.lastTenRows || [];
                    
                    const lastRow = this.lastTenRows[this.lastTenRows.length - 1];

                    // Only set endIndex from sample if no saved value exists
                    if (lastRow?.rowNumber && !this.model.endIndex) {
                        this.model.endIndex = lastRow.rowNumber;
                    }

                    const headerRow = this.firstTenRows[0];
                    const colCount = headerRow?.rowValues?.length || 0;

                    this.isWideTable = colCount > 12;
                },
                error: (error) => {
                    if (error.status === 400) {
                        this.toastService.show('Bad request. Please try again.', 'info');
                    }
                    else if (error.status === 404) {
                        // Check for FILE_NOT_FOUND error
                        const errorBody = error.response ? JSON.parse(error.response) : null;

                        if (errorBody?.code === 'FILE_NOT_FOUND') {
                            this.toastService.show('Excel file not found. Delete this import batch and upload again.', 'warning');
                        }
                        else {
                            this.toastService.show('File not found. Please try again.', 'info');
                        }
                    }
                    else {
                        this.errorService.handleError(error);
                    }

                    this.loading = false;
                },
                complete: () => {
                    this.loading = false;
                }
            });
    }

    isValid(): boolean {
        return true;
    }

    onActivate(): void {
        this.route.paramMap.subscribe(params => {
            this.batchId = Number(params.get('id'));

            if (this.batchId > 0) {
                // Always reload the saved range values from the database
                this.getImportRowMapping();

                // Only fetch the file preview on first activation.
                // On back-navigation the rows are already in memory — no need to hit the server again.
                if (this.firstTenRows.length === 0) {
                    this.getBatch();
                }
            }
        });
    }

    onNext(): Observable<boolean> {
        return new Observable<boolean>(observer => {
            this.annexureService.createAnnexureImportRangeMapping(this.batchId, this.model).subscribe({
                next: () => {
                    this.toastService.show('Annexure row mapping saved successfully', 'Success');

                    const mappingsChangedKey = `annexure_mappings_changed_${this.batchId}`;
                    sessionStorage.setItem(mappingsChangedKey, 'true');

                    // Emit true on success
                    observer.next(true); 
                    observer.complete();
                },
                error: (error: any) => {
                    if (error.status === 400) {
                        this.toastService.show('Bad request. Please try again.', 'Info');
                    }
                    else {
                        this.errorService.handleError(error);
                    }

                    // Emit FALSE on FAIL
                    observer.next(false);
                    observer.complete();
                },
            });
        });
    }

    onPrevious(): Observable<boolean> {
        return scheduled([true], asyncScheduler);
    }
}
